import { createSlice } from "@reduxjs/toolkit";
import {
  getActividadActualThunk,
  getEstadoAplicacionThunk,
  getResumenAplicacionThunk,
  guardarRespuestasThunk,
  iniciarAplicacionThunk,
} from "./aplicacionTalentos.thunk";

const initialState = {
  sesionId: null,
  talento: null,
  rankingActual: null,
  estado: null,
  actividad: null,
  frases: [],
  frasePregunta: "¿Te gusta esta actividad?",
  etiquetaSi: "Sí",
  etiquetaNo: "No",
  actividadIndex: 1,
  totalActividades: 0,
  talentoIndex: 1,
  totalTalentos: 0,
  puedeAvanzar: false,
  loading: false,
  loadingRespuesta: false,
  completed: false,
  error: null,
  resumen: null,
  loadingResumen: false,
};

/* ── Helper: algunos endpoints devuelven { message, data: {...}, status_code }
      y otros devuelven el objeto directo. Esto normaliza ambos casos. ── */
const unwrap = (payload) => payload?.data ?? payload;

const aplicacionTalentosSlice = createSlice({
  name: "aplicacionTalentos",
  initialState,
  reducers: {
    resetAplicacion: () => initialState,
  },
  extraReducers: (builder) => {
    // ── Iniciar ──────────────────────────────────────
    builder
      .addCase(iniciarAplicacionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(iniciarAplicacionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const d = unwrap(action.payload);
        if (!d) return;
        state.sesionId = d.id;
        state.talento = d.talento_actual;
        state.rankingActual = d.ranking_actual;
        state.estado = d.estado;
      })
      .addCase(iniciarAplicacionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Actividad actual ─────────────────────────────
    // Respuesta directa: { talento, actividad, frases, aplicacion_usuario_id, ... }
    builder
      .addCase(getActividadActualThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActividadActualThunk.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload;
        if (!d) return;
        state.talento = d.talento ?? state.talento;
        state.actividad = d.actividad ?? state.actividad;
        state.frases = d.frases ?? [];
        state.frasePregunta =
          d.frase_pregunta ?? state.frasePregunta ?? "¿Te gusta esta actividad?";
        state.etiquetaSi = d.etiqueta_si ?? state.etiquetaSi ?? "Sí";
        state.etiquetaNo = d.etiqueta_no ?? state.etiquetaNo ?? "No";
        state.actividadIndex = d.actividad_index ?? 1;
        state.totalActividades = d.total_actividades ?? 0;
        state.talentoIndex = d.talento_index ?? 1;
        state.totalTalentos = d.total_talentos ?? 0;
        state.puedeAvanzar = d.puede_avanzar ?? false;
        state.sesionId = d.aplicacion_usuario_id ?? state.sesionId;
      })
      .addCase(getActividadActualThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Guardar respuestas ───────────────────────────
    builder
      .addCase(guardarRespuestasThunk.pending, (state) => {
        state.loadingRespuesta = true;
        state.error = null;
      })
      .addCase(guardarRespuestasThunk.fulfilled, (state, action) => {
        state.loadingRespuesta = false;
        const d = unwrap(action.payload);
        if (!d) return;
        if (d.actividad_index !== undefined) {
          state.actividadIndex = d.actividad_index;
        }
        if (d.estado === "completado") {
          state.completed = true;
          state.estado = "completado";
        }
      })
      .addCase(guardarRespuestasThunk.rejected, (state, action) => {
        state.loadingRespuesta = false;
        state.error = action.payload;
      });

    // ── Estado ──────────────────────────────────────
    builder
      .addCase(getEstadoAplicacionThunk.fulfilled, (state, action) => {
        const d = action.payload;
        if (!d) return;
        state.sesionId = d.sesion_id ?? state.sesionId;
        state.talento = d.talento_actual ?? state.talento;
        state.rankingActual = d.ranking_talento_actual ?? state.rankingActual;
        state.estado = d.estado ?? state.estado;
        if (d.estado === "completado") {
          state.completed = true;
        }
      })
      .addCase(getEstadoAplicacionThunk.rejected, (state) => {
        // Sin sesión activa — no es error crítico
        state.loading = false;
      });

    // ── Resumen ────────────────────────────────────
    builder
      .addCase(getResumenAplicacionThunk.pending, (state) => {
        state.loadingResumen = true;
        state.error = null;
      })
      .addCase(getResumenAplicacionThunk.fulfilled, (state, action) => {
        state.loadingResumen = false;
        state.resumen = action.payload;
      })
      .addCase(getResumenAplicacionThunk.rejected, (state, action) => {
        state.loadingResumen = false;
        state.error = action.payload;
      });
  },
});

export const { resetAplicacion } = aplicacionTalentosSlice.actions;
export default aplicacionTalentosSlice.reducer;
